import { type Dayjs } from "dayjs"

import { type MilestoneDependency } from "../../../../../types/project-milestone-dependency.types.ts"
import { type ProjectMilestone } from "../../../../../types/project-phases.types.tsx"
import {
  sourceSideFor,
  targetSideFor
} from "../../../../constants/milestone-dependency-relationship.ts"
import { type EffectiveScheduleEntry } from "../../../../utils/milestone-effective-schedule.ts"
import { isMilestoneFinished } from "../../../../utils/milestone-schedule-state.ts"
import { type GanttRange } from "./gantt-scale.ts"

// Zoomed right out a one-week bar is under a pixel; it stays visible, and
// its arrows attach to the edge that is actually drawn.
export const MIN_BAR_PX = 4

export interface ScheduledRow {
  milestone: ProjectMilestone
  rowIndex: number
  entry: EffectiveScheduleEntry
}

export interface GanttBar {
  milestone: ProjectMilestone
  rowIndex: number
  start: Dayjs
  end: Dayjs
  left: number
  width: number
  durationDays: number
  isShifted: boolean
  shiftReason: EffectiveScheduleEntry["shiftReason"]
  /** Pixel X of the actual finish, when one is recorded — a separate fact
   * from the planned bar, never a substitute for it. */
  actualEndOffset: number | null
  /** The stretch between the planned and the actual finish; `days` is
   * positive for a late finish, negative for an early one. */
  actualVsPlanned: { left: number; width: number; days: number } | null
}

export interface GanttConnector {
  dep: MilestoneDependency
  d: string
  sourceRow: number
  targetRow: number
}

export interface GanttLayout {
  bars: GanttBar[]
  barById: Map<string, GanttBar>
  connectors: GanttConnector[]
  chartWidth: number
  totalDays: number
  todayOffset: number | null
}

/**
 * Where "Today" and "Start" scroll to, in chart px: today's line and the
 * earliest bar. Null when there is nothing to jump to.
 */
export const ganttJumpTargets = ({
  bars,
  todayOffset
}: Pick<GanttLayout, "bars" | "todayOffset">): {
  today: number | null
  start: number | null
} => ({
  today: todayOffset,
  start: bars.length ? Math.min(...bars.map((bar) => bar.left)) : null
})

const anchorX = (bar: GanttBar, side: "start" | "end") =>
  side === "start" ? bar.left : bar.left + bar.width

const rowCentre = (rowIndex: number, rowHeight: number) =>
  rowIndex * rowHeight + rowHeight / 2

function actualVsPlannedOf(
  entry: EffectiveScheduleEntry,
  xOf: (date: Dayjs) => number,
  dayWidth: number
): GanttBar["actualVsPlanned"] {
  if (!entry.actualEnd) return null
  const days = entry.actualEnd.diff(entry.end, "day")
  if (!days) return null
  const plannedEndX = xOf(entry.end) + dayWidth
  const actualEndX = xOf(entry.actualEnd) + dayWidth
  return {
    left: Math.min(plannedEndX, actualEndX),
    width: Math.abs(actualEndX - plannedEndX),
    days
  }
}

export type BarStatus = "completed" | "overdue" | "open"

/** Overdue: the bar as drawn ends before today and the milestone is not finished. */
export const barStatusFor = ({
  milestone,
  end,
  today
}: {
  milestone: Parameters<typeof isMilestoneFinished>[0]
  end: Dayjs
  today: Dayjs
}): BarStatus =>
  isMilestoneFinished(milestone)
    ? "completed"
    : end.isBefore(today, "day")
      ? "overdue"
      : "open"

export const totalDaysOf = (range: GanttRange) =>
  Math.max(range.end.diff(range.start, "day") + 1, 1)

/**
 * Every x on the chart — bars, the actual-finish marker, today, and the
 * connector paths — from one `dayWidth`, so arrows stay on their bar edges at
 * any zoom.
 */
export function buildGanttLayout({
  scheduled,
  range,
  dependencies,
  dayWidth,
  rowHeight,
  stub,
  today
}: {
  scheduled: ScheduledRow[]
  range: GanttRange
  dependencies: MilestoneDependency[]
  dayWidth: number
  rowHeight: number
  stub: number
  today?: Dayjs
}): GanttLayout {
  const totalDays = totalDaysOf(range)
  const xOf = (date: Dayjs) => date.diff(range.start, "day") * dayWidth

  const bars: GanttBar[] = scheduled.map(({ milestone, rowIndex, entry }) => {
    const durationDays = Math.max(entry.end.diff(entry.start, "day") + 1, 1)
    return {
      milestone,
      rowIndex,
      start: entry.start,
      end: entry.end,
      left: xOf(entry.start),
      width: Math.max(durationDays * dayWidth, MIN_BAR_PX),
      durationDays,
      isShifted: entry.isShifted,
      shiftReason: entry.shiftReason,
      actualEndOffset: entry.actualEnd ? xOf(entry.actualEnd) + dayWidth : null,
      actualVsPlanned: actualVsPlannedOf(entry, xOf, dayWidth)
    }
  })
  const barById = new Map(bars.map((bar) => [bar.milestone.id, bar]))

  const connectors: GanttConnector[] = []
  dependencies.forEach((dep) => {
    const source = barById.get(dep.source_milestone)
    const target = barById.get(dep.target_milestone)
    if (!source || !target) return
    const sourceSide = sourceSideFor(dep.type)
    const sx = anchorX(source, sourceSide)
    const sy = rowCentre(source.rowIndex, rowHeight)
    const tx = anchorX(target, targetSideFor(dep.type))
    const ty = rowCentre(target.rowIndex, rowHeight)
    const stubX =
      sx +
      (sourceSide === "end" ? 1 : -1) *
        (stub + Math.max(dep.lag_days, 0) * dayWidth)
    connectors.push({
      dep,
      d: `M ${sx} ${sy} H ${stubX} V ${ty} H ${tx}`,
      sourceRow: source.rowIndex,
      targetRow: target.rowIndex
    })
  })

  const day = today?.startOf("day")
  const todayOffset =
    day && !day.isBefore(range.start, "day") && !day.isAfter(range.end, "day")
      ? xOf(day)
      : null

  return {
    bars,
    barById,
    connectors,
    chartWidth: totalDays * dayWidth,
    totalDays,
    todayOffset
  }
}

const BAR_LABEL_PX = 96
const BAR_LABEL_GAP = 8

/**
 * Where a bar's date label goes. Never past either edge of the chart: an
 * overhanging label would widen a fitted chart and bring its scrollbar back.
 */
export function barLabelSide(
  bar: { left: number; width: number },
  chartWidth: number
): "after" | "before" | "inside" | "hidden" {
  if (bar.left + bar.width + BAR_LABEL_GAP + BAR_LABEL_PX <= chartWidth)
    return "after"
  if (bar.left - BAR_LABEL_GAP - BAR_LABEL_PX >= 0) return "before"
  if (bar.width >= BAR_LABEL_PX + 2 * BAR_LABEL_GAP) return "inside"
  return "hidden"
}

export type ConnectorEmphasis = "highlight" | "dim" | "normal"

/**
 * A hovered (or focused) bar shows its own predecessor and successor links;
 * otherwise a selected milestone shows its whole chain.
 */
export function connectorEmphasis(
  dep: MilestoneDependency,
  { hoveredId, chain }: { hoveredId: string | null; chain: Set<string> | null }
): ConnectorEmphasis {
  if (hoveredId) {
    return dep.source_milestone === hoveredId ||
      dep.target_milestone === hoveredId
      ? "highlight"
      : "dim"
  }
  if (chain) {
    return chain.has(dep.source_milestone) && chain.has(dep.target_milestone)
      ? "highlight"
      : "dim"
  }
  return "normal"
}

export interface RowWindow {
  first: number
  last: number
}

const OVERSCAN_ROWS = 6

/** The rows to keep in the page: those in view plus a few either side. */
export function visibleRowWindow({
  scrollTop,
  viewportHeight,
  rowHeight,
  rowCount,
  overscan = OVERSCAN_ROWS
}: {
  scrollTop: number
  viewportHeight: number
  rowHeight: number
  rowCount: number
  overscan?: number
}): RowWindow {
  const lastRow = Math.max(rowCount - 1, 0)
  if (viewportHeight <= 0) return { first: 0, last: lastRow }
  const first = Math.floor(scrollTop / rowHeight) - overscan
  const last = Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan
  return { first: Math.max(0, first), last: Math.min(lastRow, last) }
}

/** An arrow is drawn when any part of its vertical run crosses the window. */
export const connectorsInRows = (
  connectors: GanttConnector[],
  window: RowWindow
): GanttConnector[] =>
  connectors.filter(
    ({ sourceRow, targetRow }) =>
      Math.max(sourceRow, targetRow) >= window.first &&
      Math.min(sourceRow, targetRow) <= window.last
  )

// Half the "Today" badge's width, with a little room to spare.
const TODAY_BADGE_HALF_PX = 26

/**
 * Centred on the today line, unless that would cut the badge off at either
 * edge of the chart; then it hangs inward from the line.
 */
export const todayBadgeAlign = (
  todayOffset: number,
  chartWidth: number
): "start" | "center" | "end" =>
  todayOffset < TODAY_BADGE_HALF_PX
    ? "start"
    : todayOffset > chartWidth - TODAY_BADGE_HALF_PX
      ? "end"
      : "center"
